import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ClientIdentificationRequest } from '@api/src/models/AgentApi/Contract/client-identification-request';
import { AbstractForm } from '@core/abstract/form';
import { MediumType } from '@shared/data/medium-type';
import { convertPhoneToDb } from '@shared/utils/phone';
import { format } from 'date-fns';

export enum ClientSearchFormMode {
    Medium,
    MdiumSid,
    MobilePhone,
    Email,
}

export enum ClientSearchFormField {
    MediumSid = 'mediumSid',
    MediumUid = 'mediumUid',
    Mobnum = 'mobnum',
    PhoneCode = 'phoneCode',
    MediumN = 'mediumN',
    // eslint-disable-next-line no-shadow
    MediumType = 'mediumType',
    Email = 'email',
    ExpDateMonth = 'expDateMonth',
    ExpDateYear = 'expDateYear',
}

export type ClientSearchFormModel = {
    [ClientSearchFormField.MediumSid]: FormControl<string>;
    [ClientSearchFormField.MediumUid]: FormControl<string>;
    [ClientSearchFormField.Mobnum]: FormControl<string>;
    [ClientSearchFormField.MediumN]: FormControl<string>;
    [ClientSearchFormField.MediumType]: FormControl<MediumType>;
    [ClientSearchFormField.Email]: FormControl<string>;
    [ClientSearchFormField.PhoneCode]: FormControl<string>;
    [ClientSearchFormField.ExpDateMonth]: FormControl<string>;
    [ClientSearchFormField.ExpDateYear]: FormControl<number>;
};

@Injectable()
export class ClientSearchFormService extends AbstractForm<ClientSearchFormField, ClientSearchFormModel> {
    public createReactiveForm(): FormGroup<ClientSearchFormModel> {
        this.build({
            [ClientSearchFormField.MediumSid]: new FormControl<string>(
                {
                    value: null,
                    disabled: true,
                },
                [Validators.required],
            ),
            [ClientSearchFormField.MediumUid]: new FormControl<string>(
                {
                    value: null,
                    disabled: true,
                },
                [Validators.required],
            ),
            [ClientSearchFormField.Mobnum]: new FormControl<string>(
                {
                    value: null,
                    disabled: true,
                },
                [Validators.required],
            ),
            [ClientSearchFormField.PhoneCode]: new FormControl<string>(null, [Validators.required]),
            [ClientSearchFormField.MediumN]: new FormControl<string>(null, [Validators.required]),
            [ClientSearchFormField.MediumType]: new FormControl<MediumType>(null, [Validators.required]),
            [ClientSearchFormField.ExpDateMonth]: new FormControl<string>(
                {
                    value: null,
                    disabled: true,
                },
                [Validators.required],
            ),
            [ClientSearchFormField.ExpDateYear]: new FormControl<number>(
                {
                    value: null,
                    disabled: true,
                },
                [Validators.required, Validators.maxLength(4), Validators.minLength(4)],
            ),
            [ClientSearchFormField.Email]: new FormControl<string>(
                {
                    value: null,
                    disabled: true,
                },
                [Validators.required],
            ),
        });

        this.defineMechanics();

        return this.form;
    }

    public getFormValueBySearchType(searchType: ClientSearchFormMode): ClientIdentificationRequest {
        switch (searchType) {
            case ClientSearchFormMode.Medium:
                return {
                    ...{
                        mediumType: this.getValueByPath(ClientSearchFormField.MediumType),
                        mediumN: this.getValueByPath(ClientSearchFormField.MediumN),
                    },
                    ...(this.getValueByPath(ClientSearchFormField.MediumType) === MediumType.BankCard
                        ? {
                              expDate: format(
                                  new Date(
                                      this.getValueByPath(ClientSearchFormField.ExpDateYear),
                                      Number(this.getValueByPath(ClientSearchFormField.ExpDateMonth)) - 1,
                                      15,
                                  ),
                                  'yyyy-MM-dd',
                              ),
                          }
                        : {}),
                };
            case ClientSearchFormMode.MdiumSid:
                return {
                    mediumType: MediumType.SmartToken,
                    mediumSid: this.getValueByPath(ClientSearchFormField.MediumSid),
                };
            case ClientSearchFormMode.MobilePhone:
                return {
                    mobnum: convertPhoneToDb(
                        this.getValueByPath(ClientSearchFormField.Mobnum),
                        this.getValueByPath(ClientSearchFormField.PhoneCode),
                    ),
                };
            case ClientSearchFormMode.Email:
                return { email: this.getValueByPath(ClientSearchFormField.Email) };
            default:
                return null;
        }
    }

    private defineMechanics(): void {
        this.watchForValueChanges(ClientSearchFormField.MediumType).subscribe((mediumType: MediumType) => {
            if (mediumType === MediumType.BankCard) {
                this.getControlByPath(ClientSearchFormField.ExpDateMonth).enable();
                this.getControlByPath(ClientSearchFormField.ExpDateYear).enable();
            } else {
                this.getControlByPath(ClientSearchFormField.ExpDateMonth).disable();
                this.getControlByPath(ClientSearchFormField.ExpDateYear).disable();
            }
        });
    }
}
